'use client';
import SmartLink from '@/components/SmartLink';
import { cn } from '@/lib/utils';
import React from 'react';

export type ButtonVariant = 'primary' | 'secondary';

export type ButtonProps = {
	text?: string;
	content?: React.ReactNode;
	href?: string;
	variant?: ButtonVariant;
	disabled?: boolean;
	className?: string;
	newTab?: boolean;
	ariaLabel?: string;
};

const Button = ({
	text,
	href,
	variant = 'primary',
	disabled = false,
	className,
	newTab,
	ariaLabel,
	content,
}: ButtonProps) => {
	const base =
		'inline-flex items-center justify-center min-w-[104px] rounded-sm py-2 text-center text-base font-semibold leading-[20px]';

	const variants = {
		primary: 'bg-otnLime text-black border-otnLime',
		secondary: 'bg-transparent border-4 text-otnBlue border-otnBlue',
	};

	// TODO: update later when designs are finalized
	const disabledStyles = {
		primary: 'bg-otnLime/40 text-white/40 border-otnLime/40 cursor-not-allowed',
		secondary: 'bg-transparent border-4 border-otnBlue/40 text-white/40 cursor-not-allowed',
	};

	const colorClasses = disabled ? disabledStyles[variant] : variants[variant];

	const classes = cn(base, colorClasses, className);

	const finalHref = disabled ? '#' : (href ?? '#');

	const commonDisabledProps = disabled
		? {
				'aria-disabled': 'true' as const,
				tabIndex: -1,
				onClick: (e: React.MouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
				},
				'aria-label': ariaLabel,
			}
		: {
				'aria-label': ariaLabel,
			};
	const renderedContent = content ?? text;

	return (
		<SmartLink href={finalHref} className={classes} newTab={newTab} {...commonDisabledProps}>
			{renderedContent}
		</SmartLink>
	);
};

export default Button;
